import getUserAvatar from 'application/common/utils/getUserAvatar';
import { API } from 'aws-amplify';

export const getUserAndTeamData = (game: any) => {
  // Handle both IndividualGame and TeamGame types
  if ('users' in game) {
    return game.users?.items || [];
  } else if ('circles' in game) {
    return game.circles?.items || [];
  }
  return [];
};

export const transformToTableData = async (
  items: any[],
  contestType: string
) => {
  if (!items) return [];
  return Promise.all(
    items.map(async (item) => ({
      position: item.position?.toString(),
      name: item.user?.name || '',
      score: item.doneActionCount?.toString() || '0',
      editID: item.id,
      profileType: item.user?.scope || 'person',
      users: item.circle_members || [],
      id: item.user?.id,
      imageName: item.user?.imageName,
      initials: item.user?.initials || '',
      doneCountFormatted: item.doneActionCount?.toString() || '0',
      progressPercent: item.goalActionCount
        ? (item.doneActionCount / item.goalActionCount) * 100
        : 0,
      profilePicture: await getUserAvatar(
        item.user?.imageName,
        item.user?.imageType,
        true
      ),
      contestType,
      missions: item.missions || [], // Add missions data for incentive games
    }))
  );
};

export const transformData = (items: any[]) => {
  return items.map((item) => ({
    position: item.position?.toString(),
    name: item.name,
    score: item.score || item.doneCountFormatted || '0',
    editID: item.editID || item.id,
    profileType: item.profileType,
    users: item.users || [],
    id: item.id,
    imageName: item.imageName,
    initials: item.initials || '',
    missions: item.missions || [], // Preserve missions data for incentive games
  }));
};

export const getDoneCountOrPercent = (
  doneCountFormatted: string,
  progressPercent: number,
  contestType: string
) => {
  if (!window.appSettings?.features?.leader?.hideProgressCircle) {
    if (contestType === 'percent_to_goal') {
      return `${progressPercent}%`;
    }
    return doneCountFormatted?.toString();
  }
  return doneCountFormatted?.toString();
};

// ---------------- Incentive enrichment ----------------
const getLeaderboardsQuery = /* GraphQL */ `
  query GetLeaderboards($request: AWSJSON!) {
    getLeaderboards(request: $request)
  }
`;

const parseFlex = (flexFields: any) => {
  try {
    if (!flexFields) return {};
    return typeof flexFields === 'string' ? JSON.parse(flexFields) : flexFields;
  } catch (e) {
    return {};
  }
};

const buildKpiRuleMap = (rules: any[]) => {
  const map: Record<string, any> = {};
  (rules || []).forEach((r: any) => {
    if (r?.id) map[r.id] = r;
  });
  return map;
};

const toNumber = (value: any) => {
  if (value === null || value === undefined) return 0;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const sortBySequence = (arr: any[] = []) => {
  return [...arr].sort((a, b) => {
    const aSeq = Number(a?.sequence ?? 0);
    const bSeq = Number(b?.sequence ?? 0);
    return aSeq - bSeq;
  });
};

const normalizeMissions = (missions?: any[]) => {
  if (!Array.isArray(missions)) return [];

  const normalised = missions.map((mission: any) => ({
    ...mission,
    actual: toNumber(mission?.actual ?? mission?.doneActionCount),
    goal: toNumber(mission?.goal ?? mission?.goalActionCount),
  }));

  return sortBySequence(normalised);
};

const normalizeCircleMembers = (members?: any[]) => {
  if (!Array.isArray(members)) return [];

  const normalisedMembers = members.map((member: any) => ({
    ...member,
    actual: toNumber(member?.actual ?? member?.doneActionCount),
    goal: toNumber(member?.goal ?? member?.goalActionCount),
    kpis: sortBySequence(
      Array.isArray(member?.kpis)
        ? member.kpis.map((kpi: any) => ({
            ...kpi,
            points: toNumber(kpi?.points ?? kpi?.actual ?? 0),
            goal: toNumber(kpi?.goal ?? kpi?.goalActionCount),
          }))
        : []
    ),
  }));

  return sortBySequence(normalisedMembers);
};

export const enrichIncentiveProgress = async (game: any, items: any[]) => {
  if (!game || !items?.length) return items;

  const scope = game?.scope || 'person';

  const normalizedItems = items.map((item: any) => ({
    ...item,
    missions: normalizeMissions(item?.missions),
    users: normalizeCircleMembers(item?.users),
  }));

  const hasBackendData = normalizedItems.some((item) =>
    scope === 'circle' ? item.users.length > 0 : item.missions.length > 0
  );

  if (hasBackendData) {
    return normalizedItems;
  }

  const flex = parseFlex(game.flexFields);
  const mapping = flex?.incentive?.kpiToMissionID || {};
  const ruleMap = buildKpiRuleMap(game?.rules || []);
  const entries = Object.entries(mapping) as Array<[string, string]>; // [kpiId, missionId]

  if (entries.length === 0) {
    return normalizedItems;
  }

  const childResults = await Promise.all(
    entries.map(async ([kpiId, missionId]) => {
      try {
        const res: any = await API.graphql({
          query: getLeaderboardsQuery,
          variables: { request: JSON.stringify({ missionID: missionId }) },
        });
        const jsonRes = JSON.parse(res?.data?.getLeaderboards || '{}');
        const mission = jsonRes?.Items?.[0];
        const users = mission?.users?.items || [];
        return [kpiId, users] as [string, any[]];
      } catch (e) {
        return [kpiId, []] as [string, any[]];
      }
    })
  );

  const kpiToUsers: Record<string, any[]> = {};
  childResults.forEach(([k, v]) => (kpiToUsers[k] = v));

  if (scope === 'person') {
    const missionsByUser: Record<string, any[]> = {};

    entries.forEach(([kpiId, missionId]) => {
      const rule = ruleMap[kpiId] || {};
      (kpiToUsers[kpiId] || []).forEach((userEntry: any) => {
        const userId = userEntry?.user?.id;
        if (!userId) return;

        missionsByUser[userId] = [
          ...(missionsByUser[userId] || []),
          {
            KPI: rule?.name || rule?.title || 'KPI',
            missionID: missionId,
            actual: toNumber(userEntry?.doneActionCount),
            goal: toNumber(rule?.goal ?? 0),
          },
        ];
      });
    });

    return normalizedItems.map((item: any) => {
      const fallbackMissions = missionsByUser[item?.id];
      if (!fallbackMissions?.length) {
        return item;
      }

      return {
        ...item,
        missions: normalizeMissions(fallbackMissions),
      };
    });
  }

  // scope === 'circle': fallback enrichment is more complex, so return normalized items as-is
  return normalizedItems;
};
